<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageImage from '@/components/common/BackstageImage.vue';
import { useAnalytics } from '@/composables/analytics';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { nextTick, ref } from 'vue';

export interface Props {
  src: string;
  poster?: string;
  title?: string;
  fallbackText?: string;
}

const props = defineProps<Props>();

const { addCustomGaEvent } = useAnalytics();

const isPlaying = ref(false);
const videoRef = ref<HTMLVideoElement | null>(null);

async function playVideo() {
  addCustomGaEvent({
    event: GaCustomEvents.VideoTutorialPlay,
    video_title: props.title,
  });
  isPlaying.value = true;
  await nextTick();
  videoRef.value?.play();
}
</script>

<template>
  <div class="backstage-video-container" @contextmenu.prevent>
    <video
      v-if="props.src && isPlaying"
      ref="videoRef"
      controlsList="nodownload noremoteplayback"
      controls
      autoplay
      class="backstage-video"
      disablePictureInPicture>
      <source :src="props.src" />
      {{ props.fallbackText ?? 'Your browser does not support the video tag.' }}
    </video>
    <BackstageImage
      v-if="props.src && props.poster && !isPlaying"
      :src="props.poster"
      :alt="props.title ?? 'Video thumbnail'"
      class="backstage-video-poster"
      @click="playVideo" />

    <div v-if="props.src && !isPlaying" class="backstage-video-play-overlay" @click="playVideo">
      <div class="backstage-video-play-button">
        <BackstageIcon :name="Icons.PLAY" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.backstage-video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;

  .backstage-video {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
  }

  .backstage-video-poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  .backstage-video-play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: rgb(0, 0, 0, 0.2);
    }

    .backstage-video-play-button {
      width: 48px;
      height: 48px;
      background: var(--primary-bg-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--primary-text-color);
      &:hover {
        background-color: rgb(255, 255, 255, 0.8);
      }
    }
  }
}
</style>
